// [PHASE-5 2026-10] How an API error ends (or narrows) the session.
//
// Pure: no React, no axios instance, no globals beyond the storage passed in.
// App.js's response interceptor asks classifyApiError() what an error means:
//
//   * 403 {code: "SUBSCRIPTION_EXPIRED", renewal_only: true}
//       → { kind: 'renewal', lock }   the school_admin sees the renewal screen
//   * 403 {code: "SUBSCRIPTION_EXPIRED", renewal_only: false}
//       → { kind: 'signout', reason: 'subscription', lock }
//   * 401 carrying X-Session-Ended (expired | revoked | max_age)
//       → { kind: 'signout', reason }
//   * anything else → null (handled as before)
//
// Errors from the sign-in endpoints themselves are never classified: there is
// no session yet, and the login form shows those messages itself.

export const SUBSCRIPTION_LOCK_CODE = 'SUBSCRIPTION_EXPIRED';
export const SESSION_ENDED_HEADER = 'x-session-ended';
export const SESSION_END_REASONS = ['expired', 'revoked', 'max_age'];

// sessionStorage key for the one-line notice LoginNotice shows after a forced
// sign-out (survives the redirect to /login, dies with the tab).
export const LOGIN_NOTICE_KEY = 'edugate_login_notice';

const SIGN_IN_PATHS = [/\/auth\/login(\/|$|\?)/, /\/auth\/reset-(request|password)(\/|$|\?)/];

// The sign-in forms (password, PIN, password reset) show their own errors,
// a lock included; nothing global should add a second message.
export function isSignInRequest(config) {
  const url = (config && config.url) || '';
  return SIGN_IN_PATHS.some((re) => re.test(url));
}

function headerValue(headers, name) {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    const v = headers.get(name);
    if (v !== undefined && v !== null) return v;
  }
  const lower = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lower) return headers[key];
  }
  return undefined;
}

export function isSubscriptionLock(detail) {
  return !!detail && typeof detail === 'object' && detail.code === SUBSCRIPTION_LOCK_CODE;
}

export function classifyApiError(error) {
  const response = error && error.response;
  if (!response) return null;
  if (isSignInRequest(error.config)) return null;

  const detail = response.data && response.data.detail;
  if (response.status === 403 && isSubscriptionLock(detail)) {
    return detail.renewal_only
      ? { kind: 'renewal', lock: detail }
      : { kind: 'signout', reason: 'subscription', lock: detail };
  }
  if (response.status === 401) {
    const raw = headerValue(response.headers, SESSION_ENDED_HEADER);
    const reason = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (SESSION_END_REASONS.includes(reason)) return { kind: 'signout', reason };
  }
  return null;
}

// ── When the session ends, on THIS device's clock ──────────────────────────
// The server says how many seconds are left (expires_in at login,
// session_seconds_left from /auth/me); the client adds them to its own clock.
// Comparing the server's absolute session_expires_at with Date.now() signed a
// tablet whose clock ran a day fast out on every login.
export function sessionEndFromSecondsLeft(secondsLeft, now = Date.now()) {
  const s = typeof secondsLeft === 'number' ? secondsLeft : Number.NaN;
  return Number.isFinite(s) && s > 0 ? now + s * 1000 : null;
}

// ── Login notice (written at sign-out, read once on the login screen) ──────

export function saveLoginNotice(storage, reason, lock) {
  if (!storage) return;
  const notice = { reason };
  if (lock) {
    notice.message = lock.message || '';
    notice.message_fr = lock.message_fr || '';
    notice.message_en = lock.message_en || '';
  }
  try {
    storage.setItem(LOGIN_NOTICE_KEY, JSON.stringify(notice));
  } catch {
    /* storage unavailable: the user just lands on the login screen */
  }
}

export function takeLoginNotice(storage) {
  if (!storage) return null;
  let raw = null;
  try {
    raw = storage.getItem(LOGIN_NOTICE_KEY);
    storage.removeItem(LOGIN_NOTICE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.reason === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

// i18n key for a notice's reason; a subscription notice carries its own
// server message (all three languages) and uses this only as a fallback.
export function loginNoticeKey(reason) {
  switch (reason) {
    case 'max_age': return 'session_notice_max_age';
    case 'expired': return 'session_notice_expired';
    case 'subscription': return 'session_notice_subscription';
    default: return 'session_notice_revoked';
  }
}

// The server's message in the UI language, or '' to use the i18n fallback.
export function localizedServerMessage(obj, language) {
  if (!obj) return '';
  const lang = (language || 'ar').slice(0, 2);
  if (lang === 'fr' && obj.message_fr) return obj.message_fr;
  if (lang === 'en' && obj.message_en) return obj.message_en;
  return obj.message || obj.message_en || obj.message_fr || '';
}
